import React, { ErrorInfo, ReactNode } from 'react';
import { ErrorPage } from './ErrorPage';
import { trackInsightsException } from '../../utils/telemetry/appinsights';

/*
This is class component because react docs say that equivalent
functionality can not be done with function component
*/

export class ErrorBoundary extends React.Component<{ children: ReactNode }, { hasError: boolean }> {
  // Incremented in componentDidCatch so the deferred reset can tell whether a
  // new error was caught between the navigation event and the setTimeout firing.
  private catchCount = 0;

  // Stored as a class arrow property so the same reference is used for both
  // addEventListener and removeEventListener.
  private handleLocationChange = (): void => {
    const countAtNavigation = this.catchCount;
    // Defer to avoid calling setState while React is running useInsertionEffect
    // (e.g. styled-components style injection during navigation), which React 19
    // forbids and causes the component tree to unmount.
    // Guard against the race where a new page throws between the navigation event
    // and this timeout: if catchCount changed, a new error was caught and we must
    // not clear it.
    setTimeout(() => {
      if (this.catchCount === countAtNavigation) {
        this.resetError();
      }
    }, 0);
  };

  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: true } {
    return { hasError: true };
  }

  resetError = (): void => {
    this.setState({ hasError: false });
  };

  componentDidMount(): void {
    window.addEventListener('locationchange', this.handleLocationChange);
  }

  componentWillUnmount(): void {
    window.removeEventListener('locationchange', this.handleLocationChange);
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.catchCount++;
    trackInsightsException('FRONTEND_ERROR_BOUNDARY', error, {
      info,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorPage />;
    }

    return this.props.children;
  }
}
